import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BlockUi from "react-block-ui";
import { Card } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-input-2";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";

import ServiceReferral from "../../../api/ServiceReferral.js";
import ProfileService from "../../../api/ProfileService";
import MyYuukke from "../../../api/MyYuukke";
import RazorPay from "../../../components/RazorPay";
import ServiceLoginModel from "../../Forms/ServiceLandingV2/ServiceLoginModel";
import { API_URL, REACT_APP_SITE_KEY } from "../../../config/app-config";
import {
  getCategoryListV2,
  getSubscriptionList,
} from "../../../redux/actions/calendarAction";
import { getAllCommonLanguageList } from "../../../redux/actions/commonApiActions";

import headerImg from "../../../images/service/yuukke-logo.png";
import "../../ServicMarkertplacePages/YuukkeLanding.css";

// ─────────────────────────────────────────────────────────────────────────────
// STYLES (Redesigned Split-Screen Layout + Extra Content)
// ─────────────────────────────────────────────────────────────────────────────
const styles = `
.svcrefpg-root {
  min-height: 100vh; 
  background: #fdf8f9; 
  display: flex; 
  flex-direction: column;
  align-items: center; 
  justify-content: center; 
  padding: 2rem 1.25rem;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.svcrefpg-container {
  display: flex;
  flex-direction: row;
  max-width: 90rem;
  width: 100%;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 24px 50px rgba(122,0,36,0.08), 0 4px 15px rgba(0,0,0,0.03);
  overflow: hidden;
}

/* Left side - Hero / Welcome */
.svcrefpg-hero {
  flex: 1;
  background: linear-gradient(145deg, #94002c, #7a0024);
  color: #fff;
  padding: 3.5rem 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.svcrefpg-hero::before, .svcrefpg-hero::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}
.svcrefpg-hero::before { width: 300px; height: 300px; top: -100px; left: -100px; }
.svcrefpg-hero::after { width: 200px; height: 200px; bottom: -50px; right: -50px; }

.svcrefpg-hero-content {
  position: relative;
  z-index: 1;
}

.svcrefpg-logo { 
  width: 120px; 
  margin-bottom: 2.5rem; 
  filter: brightness(0) invert(1);
}

.svcrefpg-hero-title {
  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1rem;
}

.svcrefpg-hero-subtitle {
  font-size: 1.1rem;
  line-height: 1.6;
  opacity: 0.9;
  margin-bottom: 1.5rem;
}

.svcrefpg-code-badge {
  display: inline-flex; 
  align-items: center; 
  gap: 10px;
  background: rgba(255, 255, 255, 0.15); 
  border: 1px solid rgba(255,255,255,0.3); 
  border-radius: 12px;
  padding: 12px 24px; 
  backdrop-filter: blur(10px);
  font-family: ui-monospace, monospace;
  font-size: 1.2rem; 
  font-weight: 700; 
  letter-spacing: 0.1em;
  color: #fff; 
}

/* NEW CONTENT STYLES: Benefits & Trust Section */
.svcrefpg-benefits {
  margin-top: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.svcrefpg-benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}
.svcrefpg-benefit-icon {
  font-size: 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}
.svcrefpg-benefit-text h4 {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 4px;
  color: #fff;
}
.svcrefpg-benefit-text p {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: 1.5;
}

.svcrefpg-trust {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 12px;
}
.svcrefpg-trust p {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 500;
  line-height: 1.4;
}

/* Right side - Form Area */
.svcrefpg-form-area {
  flex: 1.3;
  padding: 3.5rem 3rem;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.svcrefpg-icon-wrap {
  width: 56px; height: 56px; border-radius: 50%; margin: 0 0 1.25rem;
  display: flex; align-items: center; justify-content: center; font-size: 24px;
}
.svcrefpg-icon-wrap.loading { background: #f0dde3; }
.svcrefpg-icon-wrap.valid   { background: #dcfce7; }
.svcrefpg-icon-wrap.invalid { background: #fee2e2; }

.svcrefpg-title { font-size: 1.5rem; font-weight: 700; color: #111; margin: 0 0 10px; }
.svcrefpg-sub   { font-size: 0.95rem; color: #6b4550; margin: 0 0 24px; line-height: 1.6; }

.svcrefpg-spinner {
  width: 26px; height: 26px; border-radius: 50%;
  border: 3px solid #f0dde3; border-top-color: #a00030;
  animation: svcrefpg-spin 0.7s linear infinite;
}
@keyframes svcrefpg-spin { to { transform: rotate(360deg); } }

.svcrefpg-btn {
  width: 100%; height: 52px; border-radius: 12px; border: none;
  background: #7a0024; color: #fff; font-size: 1rem; font-weight: 700;
  cursor: pointer; transition: background 0.2s, transform 0.1s; margin-bottom: 12px;
}
.svcrefpg-btn:hover { background: #5a001a; transform: translateY(-1px); }
.svcrefpg-btn-outline {
  width: 100%; height: 52px; border-radius: 12px;
  background: #fff; border: 1.5px solid #f0dde3; color: #7a0024;
  font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: all 0.2s;
}
.svcrefpg-btn-outline:hover { background: #fff8f9; border-color: #d99cae; }

/* Manual correction box */
.svcrefpg-fix-box { text-align: left; margin-bottom: 24px; background: #fafafa; padding: 1.5rem; border-radius: 16px; }
.svcrefpg-fix-label { font-size: 0.85rem; font-weight: 700; color: #444; margin-bottom: 8px; display: block; }
.svcrefpg-fix-row {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1.5px solid #e5e5e5; border-radius: 12px;
  padding: 0 16px; height: 52px; margin-bottom: 8px; transition: border-color 0.2s;
}
.svcrefpg-fix-row:focus-within { border-color: #7a0024; }
.svcrefpg-fix-row.is-valid { border-color: #22c55e; }
.svcrefpg-fix-input {
  flex: 1; border: none; outline: none; height: 100%;
  font-size: 1.1rem; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: #7a0024; background: transparent;
  font-family: ui-monospace, monospace;
}
.svcrefpg-fix-hint { font-size: 0.8rem; color: #888; }

/* Responsive adjustments */
@media (max-width: 850px) {
  .svcrefpg-container { flex-direction: column; }
  .svcrefpg-hero { padding: 2.5rem 1.5rem; align-items: center; text-align: center; }
  .svcrefpg-hero::before, .svcrefpg-hero::after { display: none; }
  .svcrefpg-logo { margin-bottom: 1.5rem; }
  .svcrefpg-hero-title { font-size: 1.8rem; }
  .svcrefpg-benefits { text-align: left; margin-top: 2rem; }
  .svcrefpg-trust { margin-top: 2rem; flex-direction: column; text-align: center; }
  .svcrefpg-form-area { padding: 2.5rem 1.5rem; }
  .svcrefpg-icon-wrap { margin: 0 auto 1.25rem; }
  .svcrefpg-title, .svcrefpg-sub { text-align: center; }
}
`;

let injected = false;
function injectStylesOnce() {
  if (injected || typeof document === "undefined") return;
  const tag = document.createElement("style");
  tag.setAttribute("data-svcrefpg-styles", "true");
  tag.innerHTML = styles;
  document.head.appendChild(tag);
  injected = true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Normalizes what a human actually types/pastes into a URL param
// ─────────────────────────────────────────────────────────────────────────────
function normalizeReferralCode(raw) {
  if (!raw) return "";
  let code = decodeURIComponent(raw).trim().toUpperCase();
  code = code.replace(/[^A-Z0-9]/g, "");
  return code;
}

function buildLookalikeVariants(code) {
  const variants = new Set([code]);
  const swaps = [["O", "0"], ["0", "O"], ["I", "1"], ["L", "1"], ["1", "I"]];
  swaps.forEach(([from, to]) => {
    if (code.includes(from)) variants.add(code.split(from).join(to));
  });
  return Array.from(variants);
}

function ripple(e) {
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const el = document.createElement("span");
  el.className = "yk2-ripple-el";
  el.style.cssText = `width:${size}px;height:${size}px;top:${
    e.clientY - rect.top - size / 2
  }px;left:${e.clientX - rect.left - size / 2}px`;
  btn.appendChild(el);
  el.addEventListener("animationend", () => el.remove());
}

// ─────────────────────────────────────────────────────────────────────────────
// OTP INPUT
// ─────────────────────────────────────────────────────────────────────────────
function OtpInput({ length = 6, onOtpSubmit }) {
  const [otp, setOtp] = useState(Array(length).fill(""));
  const inputs = useRef([]);
  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    onOtpSubmit(next.join(""));
    if (val && i < length - 1) inputs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
      {otp.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          style={{ width: 44, height: 52, textAlign: "center", fontSize: 22, fontWeight: 700, border: digit ? "2px solid #7a0024" : "2px solid #e0e0e0", borderRadius: 10, outline: "none", background: digit ? "#fff8f9" : "#fff", color: "#7a0024", transition: "border-color 0.2s, background 0.2s", caretColor: "#7a0024" }}
          onFocus={(e) => { e.target.style.borderColor = "#7a0024"; e.target.style.boxShadow = "0 0 0 3px rgba(122,0,36,0.1)"; }}
          onBlur={(e) => { e.target.style.borderColor = digit ? "#7a0024" : "#e0e0e0"; e.target.style.boxShadow = "none"; }}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM STEP BAR
// ─────────────────────────────────────────────────────────────────────────────
function FormStepBar({ active, total }) {
  return (
    <div className="yk2-form-step-bar">
      {Array.from({ length: total }, (_, i) => (
        <React.Fragment key={i}>
          <div className={`yk2-form-step-dot ${i < active ? "done" : i === active ? "active" : ""}`}>
            {i < active ? "✓" : i + 1}
          </div>
          {i < total - 1 && <div className={`yk2-form-step-line${i < active ? " done" : ""}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PHONE UPDATE MODAL
// ─────────────────────────────────────────────────────────────────────────────
function PhoneUpdateModal({ open, onClose, existingUser, newPhone, onSuccess }) {
  const [step, setStep] = useState(0);
  const [typedOldPhone, setTypedOldPhone] = useState("");
  const [typedNewPhone, setTypedNewPhone] = useState("");
  const [oldOtpSent, setOldOtpSent] = useState(false);
  const [oldOtpId, setOldOtpId] = useState(null);
  const [newOtpId, setNewOtpId] = useState(null);
  const [oldOtp, setOldOtp] = useState("");
  const [newOtp, setNewOtp] = useState("");
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setTypedOldPhone("");
      setTypedNewPhone(newPhone || "");
      setOldOtpSent(false);
      setOldOtpId(null);
      setNewOtpId(null);
      setOldOtp("");
      setNewOtp("");
    }
  }, [open, newPhone]);

  const maskPhone = (phone) => {
    if (!phone) return "XXXXXXXXXX";
    const str = String(phone);
    if (str.length <= 4) return str;
    return str.slice(0, 2) + "X".repeat(Math.max(str.length - 4, 4)) + str.slice(-2);
  };

  const sendOtpToPhone = (phoneNumber, onSuccessCallback) => {
    setLoader(true);
    ProfileService.sendOtp({ type: "phone", phoneNumber: phoneNumber, optModelName: "Service" })
      .then((res) => {
        setLoader(false);
        if (res.data.data?.id) onSuccessCallback(res.data.data.id);
        else toast.error("Failed to send OTP — " + (res.data.message || "try again"));
      })
      .catch(() => { toast.error("Something went wrong"); setLoader(false); });
  };

  const handleConfirmChange = () => setStep(1);

  const handleSendOtpToRegistered = () => {
    if (!typedOldPhone || typedOldPhone.length < 6) { toast.warning("Enter your registered phone number"); return; }
    setLoader(true);
    ProfileService.verifyUserPhone({ email: existingUser.email, phoneNumber: typedOldPhone })
      .then((res) => {
        setLoader(false);
        if (res.data.status === 1) {
          sendOtpToPhone(typedOldPhone, (id) => { setOldOtpId(id); setOldOtpSent(true); toast.success("OTP sent to your registered number"); });
        } else if (res.data.status === 0) {
          toast.error("This number doesn't match your registered number. Please check and try again.");
        } else {
          toast.error("User not found. Please contact support.");
        }
      })
      .catch(() => { toast.error("Something went wrong"); setLoader(false); });
  };

  const verifyOldOtpAndProceed = () => {
    if (!oldOtp || oldOtp.length !== 6) { toast.warning("Enter valid 6-digit OTP"); return; }
    if (!oldOtpId) { toast.error("OTP session expired — please resend"); return; }
    setLoader(true);
    ProfileService.validateUserOtp({ otp: oldOtp, id: oldOtpId })
      .then((res) => {
        setLoader(false);
        if (res.data.data === "VALID_OTP") {
          setLoader(true);
          ProfileService.sendOtp({ type: "phone", phoneNumber: typedNewPhone, optModelName: "Service" })
            .then((otpRes) => {
              setLoader(false);
              if (otpRes.data.data?.id) { setNewOtpId(otpRes.data.data.id); setStep(2); toast.success("OTP sent to your new number"); }
              else toast.error("Failed to send OTP to new number");
            })
            .catch(() => { toast.error("Something went wrong sending OTP"); setLoader(false); });
        } else toast.error("Wrong OTP, try again");
      })
      .catch(() => { toast.error("Something went wrong"); setLoader(false); });
  };

  const verifyNewOtpAndUpdate = () => {
    if (!newOtp || newOtp.length !== 6) { toast.warning("Enter valid 6-digit OTP"); return; }
    if (!newOtpId) { toast.error("OTP session expired — please resend OTP"); return; }
    setLoader(true);
    ProfileService.validateUserOtp({ otp: newOtp, id: newOtpId })
      .then((res) => {
        setLoader(false);
        if (res.data.data === "VALID_OTP") {
          setLoader(true);
          ProfileService.updateUserPhoneNumber({ userId: existingUser.userId, phoneNumber: typedNewPhone })
            .then((res) => {
              setLoader(false);
              if (res.data.status === 1) setStep(3);
              else toast.error("Phone update failed, please try again");
            })
            .catch(() => { toast.error("Something went wrong"); setLoader(false); });
        } else toast.error("Wrong OTP — " + (res.data.data || "try again"));
      })
      .catch(() => { toast.error("Something went wrong"); setLoader(false); });
  };

  const StepIndicator = () => (
    <div style={{ display: "flex", alignItems: "center", margin: "16px 0 4px" }}>
      {[{ label: "Verify Current" }, { label: "Verify New" }, { label: "Done" }].map((s, i) => {
        const activeIdx = step === 1 ? 0 : step === 2 ? 1 : 2;
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <React.Fragment key={i}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: done ? "#22c55e" : active ? "#7a0024" : "#e5e7eb", color: done || active ? "#fff" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, transition: "all 0.3s" }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 10, marginTop: 4, color: active ? "#7a0024" : done ? "#22c55e" : "#9ca3af", fontWeight: active || done ? 600 : 400, whiteSpace: "nowrap" }}>
                {s.label}
              </span>
            </div>
            {i < 2 && <div style={{ flex: 1, height: 2, marginBottom: 18, background: done ? "#22c55e" : "#e5e7eb", transition: "background 0.3s" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <style>{`
        .pum-inner .react-tel-input .form-control { width: 100% !important; height: 44px !important; font-size: 14px !important; padding-left: 50px !important; border: 1px solid #e0e0e0 !important; border-radius: 8px !important; box-sizing: border-box !important; background: #fff !important; }
        .pum-inner .react-tel-input .flag-dropdown { border: 1px solid #e0e0e0 !important; border-right: none !important; border-radius: 8px 0 0 8px !important; background: #fafafa !important; }
        .pum-inner .react-tel-input { width: 100% !important; }
      `}</style>
      {step === 0 && (
        <>
          <DialogTitle style={{ borderBottom: "1px solid #f0f0f0" }}><span style={{ fontSize: 16, fontWeight: 600 }}>Change Phone Number?</span></DialogTitle>
          <DialogContent style={{ padding: "1.5rem" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
              <Typography variant="body1" style={{ fontWeight: 600, marginBottom: 12, color: "#222" }}>Are you sure you want to change your registered number?</Typography>
              <div style={{ background: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fff3cd", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span style={{ fontSize: 20 }}>📱</span></div>
                <div style={{ textAlign: "left" }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 2 }}>Currently registered number</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, letterSpacing: 2, color: "#222" }}>+{maskPhone(existingUser?.mobileNumber)}</Typography>
                </div>
              </div>
              <div style={{ background: "#fff8f9", border: "1px solid #f0dde3", borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fce4ec", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span style={{ fontSize: 20 }}>🆕</span></div>
                <div style={{ textAlign: "left" }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 2 }}>New number to register</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: "#7a0024" }}>+{newPhone}</Typography>
                </div>
              </div>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 20, lineHeight: 1.6 }}>Both numbers will be verified via OTP. You'll need access to your registered number to proceed.</Typography>
              <Button variant="contained" fullWidth style={{ background: "#7a0024", color: "#fff", height: 44, fontSize: 14, borderRadius: 8, marginBottom: 8 }} onClick={handleConfirmChange}>Yes, Change My Number →</Button>
              <Button variant="outlined" fullWidth style={{ color: "#7a0024", borderColor: "#f0dde3", height: 44, fontSize: 14, borderRadius: 8 }} onClick={onClose}>No, Cancel</Button>
            </div>
          </DialogContent>
        </>
      )}
      {step === 1 && (
        <>
          <DialogTitle style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}><span style={{ fontSize: 16, fontWeight: 600 }}>Verify Your Current Number</span><StepIndicator /></DialogTitle>
          <BlockUi tag="div" blocking={loader} message="Please wait...">
            <DialogContent style={{ padding: "1.5rem" }} className="pum-inner">
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
                <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.6 }}>Enter your registered phone number to receive a verification OTP.</Typography>
              </div>
              {!oldOtpSent && (
                <>
                  <div style={{ marginBottom: 20 }}>
                    <Typography variant="caption" style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#444" }}>Your Registered Phone Number *</Typography>
                    <PhoneInput country={"in"} value={typedOldPhone} onChange={(phone) => setTypedOldPhone(phone)} countryCodeEditable={false} placeholder="Enter your registered number" inputProps={{ name: "oldPhone", autoFocus: true }} containerClass="input-dark" />
                    <Typography variant="caption" style={{ color: "#888", marginTop: 4, display: "block" }}>Hint: your number ends with <strong>...{existingUser?.mobileNumber?.slice(-2)}</strong></Typography>
                  </div>
                  <Button variant="contained" fullWidth style={{ background: "#7a0024", color: "#fff", height: 44, fontSize: 14, borderRadius: 8 }} onClick={handleSendOtpToRegistered}>Verify &amp; Send OTP →</Button>
                </>
              )}
              {oldOtpSent && (
                <>
                  <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 20, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#15803d" }}><span>✓</span><span>OTP sent to <strong>+{typedOldPhone}</strong></span></div>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10, textAlign: "center" }}>Enter the 6-digit OTP</Typography>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><OtpInput length={6} onOtpSubmit={setOldOtp} /></div>
                  <Button variant="contained" fullWidth style={{ background: "#7a0024", color: "#fff", height: 44, fontSize: 14, borderRadius: 8, marginBottom: 10 }} onClick={verifyOldOtpAndProceed}>Verify OTP &amp; Continue →</Button>
                  <Typography variant="caption" style={{ color: "#7a0024", cursor: "pointer", display: "block", textAlign: "center" }} onClick={() => sendOtpToPhone(typedOldPhone, (id) => { setOldOtpId(id); toast.success("OTP resent"); })}>Didn't receive it? Resend OTP</Typography>
                </>
              )}
            </DialogContent>
          </BlockUi>
          <DialogActions style={{ padding: "8px 16px", borderTop: "1px solid #f0f0f0" }}><Button onClick={onClose} style={{ color: "#7a0024" }}>Cancel</Button></DialogActions>
        </>
      )}
      {step === 2 && (
        <>
          <DialogTitle style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}><span style={{ fontSize: 16, fontWeight: 600 }}>Verify Your New Number</span><StepIndicator /></DialogTitle>
          <BlockUi tag="div" blocking={loader} message="Please wait...">
            <DialogContent style={{ padding: "1.5rem" }} className="pum-inner">
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✉️</div>
                <div style={{ background: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <Typography variant="body2" style={{ color: "#9ca3af", textDecoration: "line-through" }}>+{typedOldPhone}</Typography>
                  <span style={{ color: "#9ca3af", fontSize: 18 }}>→</span>
                  <Typography variant="body2" style={{ color: "#7a0024", fontWeight: 700 }}>+{typedNewPhone}</Typography>
                </div>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 6 }}>Enter the OTP sent to your new number</Typography>
                <Typography variant="body1" style={{ fontWeight: 700, color: "#7a0024", marginBottom: 20 }}>+{typedNewPhone}</Typography>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><OtpInput length={6} onOtpSubmit={setNewOtp} /></div>
                <Button variant="contained" fullWidth style={{ background: "#7a0024", color: "#fff", height: 44, fontSize: 14, borderRadius: 8, marginBottom: 10 }} onClick={verifyNewOtpAndUpdate}>Verify &amp; Update Number →</Button>
                <Typography variant="caption" style={{ color: "#7a0024", cursor: "pointer" }} onClick={() => sendOtpToPhone(typedNewPhone, (id) => { setNewOtpId(id); toast.success("OTP resent to new number"); })}>Didn't receive it? Resend OTP</Typography>
              </div>
            </DialogContent>
          </BlockUi>
          <DialogActions style={{ padding: "8px 16px", borderTop: "1px solid #f0f0f0" }}><Button onClick={onClose} style={{ color: "#7a0024" }}>Cancel</Button></DialogActions>
        </>
      )}
      {step === 3 && (
        <>
          <DialogTitle style={{ borderBottom: "1px solid #f0f0f0" }}><span style={{ fontSize: 16, fontWeight: 600 }}>Phone Number Updated!</span></DialogTitle>
          <DialogContent style={{ padding: "1.5rem" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
              <Typography variant="body1" style={{ fontWeight: 600, marginBottom: 8, color: "#222" }}>Your number has been successfully updated!</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24, lineHeight: 1.6 }}>Your account is now linked to the new number and secured in our system.</Typography>
              <div style={{ background: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: 12, padding: "16px 20px", marginBottom: 24, textAlign: "left" }}>
                <Typography variant="caption" style={{ display: "block", marginBottom: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#6b7280" }}>Update Receipt</Typography>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <Typography variant="caption" color="textSecondary">Previous Number</Typography>
                  <Typography variant="body2" style={{ textDecoration: "line-through", color: "#9ca3af", fontWeight: 500 }}>+{typedOldPhone}</Typography>
                </div>
                <div style={{ borderTop: "1px dashed #e5e7eb", margin: "8px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <Typography variant="caption" color="textSecondary">New Number</Typography>
                  <Typography variant="body2" style={{ color: "#7a0024", fontWeight: 700 }}>+{typedNewPhone}</Typography>
                </div>
                <div style={{ borderTop: "1px dashed #e5e7eb", margin: "8px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Typography variant="caption" style={{ color: "#15803d", fontWeight: 700, background: "#f0fdf4", padding: "2px 8px", borderRadius: 10, border: "1px solid #bbf7d0" }}>✓ Updated &amp; Verified</Typography>
                </div>
              </div>
              <Button variant="contained" fullWidth style={{ background: "#7a0024", color: "#fff", height: 44, fontSize: 14, borderRadius: 8 }} onClick={() => { onClose(); onSuccess(typedNewPhone); }}>Continue to Payment →</Button>
            </div>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REGISTRATION FORM CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANGUAGE = [{ label: "English", value: 3, language: "ENGLISH", id: 3 }];
const DEFAULT_CATEGORY = [{ label: "General", value: 1, pId: 1 }];
const DEFAULT_ABOUT = "Service provider on Yuukke";
const DEFAULT_SERVICE_NAME = "My Service";

// ─────────────────────────────────────────────────────────────────────────────
// REGISTRATION FORM
// ─────────────────────────────────────────────────────────────────────────────
function RegistrationForm({ subscriptionList, languagelist, categoryList, onForceLogin, referralCode }) {
  const recaptcha = useRef();
  const [state, setState] = useState({
    userName: "", email: "", phoneNumber: "", otp: "", otpId: null, directoryId: null,
    loader: false, activeMenu: 0, subscriptionId: null, paymentAmount: "", initId: null,
    isAnnual: true, userId: null, try: 0, expandedPlanId: null,
  });
  const [extUsr, setExtUsr] = useState({});
  const [showExtUsr, setShowExtUsr] = useState(false);
  const [showPhoneUpdate, setShowPhoneUpdate] = useState(false);
  const [phoneUpdateUser, setPhoneUpdateUser] = useState(null);

  const updateState = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const openPaymentPage = (prize, id) => setState((prev) => ({ ...prev, paymentAmount: prize, subscriptionId: id }));

  const updatePaymentStatus = (status, id) => {
    if (status === "dismiss") {
      Swal.fire({ icon: "error", title: "Payment updating failed!", text: "If your money was debited, please contact our team at +91 04446314646", showConfirmButton: true })
        .then(() => setState((prev) => ({ ...prev, paymentAmount: null, subscriptionId: null, loader: false })));
    } else {
      updateState("loader", true);
      updateDetails(id);
    }
  };

  const sendOtp = (e, menu, skipConfirm = false) => {
    if (state.try === 4) { toast.warning("You tried maximum times, please try later!"); return; }
    if (state.phoneNumber.length < 6) { toast.warning("Enter valid phone number"); return; }
    const doSend = () => {
      updateState("loader", true);
      ProfileService.sendOtp({ type: "phone", phoneNumber: state.phoneNumber, optModelName: "Service" })
        .then((response) => {
          if (response.data.data?.id) {
            setState((prev) => ({ ...prev, otpId: response.data.data.id, loader: false, activeMenu: menu + 1, try: prev.try + 1 }));
          } else updateState("loader", false);
        })
        .catch(() => { toast.error("Something went wrong"); updateState("loader", false); });
    };
    if (skipConfirm) { doSend(); return; }
    Swal.fire({
      icon: "question", title: "Confirm Your Number",
      html: `<div style="font-size:0.95rem;color:#444;line-height:1.6;">We'll send an OTP to<br/><strong style="font-size:1.1rem;color:#7a0024;">+${state.phoneNumber}</strong><br/><br/><span style="font-size:0.85rem;color:#888;">Did you type this correctly? You won't be able to change it after verification.</span></div>`,
      confirmButtonText: "Yes, Send OTP →", confirmButtonColor: "#7a0024", cancelButtonText: "No, Let Me Fix It", showCancelButton: true, reverseButtons: true,
    }).then((result) => { if (result.isConfirmed) doSend(); });
  };

  const saveInitDetails = (e, menu) => {
    const resolvedCategory = categoryList && categoryList.length > 0 ? [{ label: categoryList[0].label, value: categoryList[0].value, pId: categoryList[0].pId || 1 }] : DEFAULT_CATEGORY;
    const resolvedLanguage = languagelist && languagelist.length > 0 ? [{ label: languagelist[0].label, value: languagelist[0].value }] : DEFAULT_LANGUAGE;
    const formData = new FormData();
    formData.append("userName", state.userName.trim());
    formData.append("email", state.email?.trim());
    formData.append("phoneNumber", state.phoneNumber?.trim());
    formData.append("serviceName", DEFAULT_SERVICE_NAME);
    formData.append("description", DEFAULT_ABOUT);
    formData.append("businessName", state.userName.trim());
    formData.append("language", JSON.stringify({ data: resolvedLanguage.map((item) => ({ language: item.label, id: item.value })) }));
    formData.append("categoryV2", JSON.stringify({ data: resolvedCategory.map((item) => ({ primaryId: item.pId, categoryId: item.value })) }));
    if (state.directoryId) formData.append("directoryId", state.directoryId);
    if (referralCode) formData.append("referralCode", referralCode);

    const config = { headers: { "content-type": "multipart/form-data" } };
    updateState("loader", true);

    ProfileService.directoryAndServiceRegisterInit(formData, config)
      .then((response) => {
        const { status, data } = response.data;
        if (status === 2) {
          setState((prev) => ({ ...prev, email: data.email, userName: data.displayName, userId: data.userId, phoneNumber: data.mobileNumber, loader: false }));
          sendOtp(e, menu, true);
          return;
        }
        if (status === 3 || status === 6) {
          setExtUsr(data);
          setState((prev) => ({ ...prev, email: data.email, userName: data.displayName, userId: data.userId, phoneNumber: data.mobileNumber, loader: false }));
          setShowExtUsr(true);
          return;
        }
        if (status === 4) {
          updateState("loader", false);
          let seconds = 3;
          Swal.fire({
            icon: "info", title: "You Already Have an Account",
            html: `<div style="font-size:0.95rem;color:#444;">You're already registered with<br/><strong>${data.email}</strong><br/><br/>Redirecting to login in <strong id="swal-countdown">${seconds}</strong> second(s)...</div>`,
            showConfirmButton: false, showCancelButton: false, allowOutsideClick: false, allowEscapeKey: false,
            didOpen: () => {
              const interval = setInterval(() => {
                seconds -= 1;
                const el = document.getElementById("swal-countdown");
                if (el) el.textContent = seconds;
                if (seconds <= 0) { clearInterval(interval); Swal.close(); onForceLogin(data.email); }
              }, 1000);
            },
          });
          return;
        }
        if (status === 5) { updateState("loader", false); setPhoneUpdateUser(data); setShowPhoneUpdate(true); return; }
        if (status === 1 && response.data?.data?.id) { updateState("initId", response.data.data.id); sendOtp(e, menu); }
      })
      .catch(() => { toast.error("Something went wrong"); updateState("loader", false); });
  };

  const verifyOtp = () => {
    if (state.otp.length !== 6) { toast.warning("Enter valid OTP"); return; }
    updateState("loader", true);
    ProfileService.validateUserOtp({ otp: state.otp, id: state.otpId })
      .then((response) => {
        if (response.data.data === "VALID_OTP") setState((prev) => ({ ...prev, activeMenu: prev.activeMenu + 1, loader: false }));
        else { toast.error("Wrong OTP"); updateState("loader", false); }
      })
      .catch(() => { toast.error("Something went wrong"); updateState("loader", false); });
  };


const updateDetails = (paymentId) => {
  const resolvedCategory = categoryList && categoryList.length > 0 ? [{ label: categoryList[0].label, value: categoryList[0].value, pId: categoryList[0].pId || 1 }] : DEFAULT_CATEGORY;
  const resolvedLanguage = languagelist && languagelist.length > 0 ? [{ label: languagelist[0].label, value: languagelist[0].value }] : DEFAULT_LANGUAGE;
  const formData = new FormData();
  formData.append("firstName", state.userName.trim());
  formData.append("lastName", state.userName.trim().substring(0, 1));
  formData.append("email", state.email);
  formData.append("phoneNumber", state.phoneNumber);
  formData.append("serviceName", DEFAULT_SERVICE_NAME);
  formData.append("description", DEFAULT_ABOUT);
  formData.append("businessName", state.userName.trim());
  formData.append("language", JSON.stringify({ data: resolvedLanguage.map((item) => ({ language: item.label, id: item.value })) }));
  formData.append("categoryV2", JSON.stringify({ data: resolvedCategory.map((item) => ({ primaryId: item.pId, categoryId: item.value })) }));
  formData.append("categoryId", resolvedCategory[0]?.value);
  formData.append("categoryName", resolvedCategory[0]?.label);
  if (state.directoryId) formData.append("directoryId", state.directoryId);
  formData.append("subscriptionId", state.subscriptionId);
  formData.append("paymentId", paymentId);
  if (state.initId) formData.append("initId", state.initId);
  if (referralCode) formData.append("referralCode", referralCode);

  const config = { headers: { "content-type": "multipart/form-data" } };
  ProfileService.directoryAndServiceRegister(formData, config)
    .then((response) => {
      if (response.data.status !== 1) {
        toast.error("Something went wrong, please try again later");
        setState((prev) => ({ ...prev, otpId: null, loader: false, otp: "", subscriptionId: null }));
        return;
      }

      // registration succeeded — the newly created user's id
      const newUserId = response.data?.data?.userId || state.userId; // confirm actual key from your register API response

      if (referralCode && newUserId) {
        ProfileService.saveReferral({
          referralCode: referralCode,
          referredUserId: newUserId,
        }).catch((err) => {
          // non-blocking: referral tracking failing should never break the signup flow
          console.error("Referral save failed:", err);
        });
      }

      setState((prev) => ({ ...prev, otpId: null, loader: false, otp: "", activeMenu: prev.activeMenu + 1, subscriptionId: null }));
    })
    .catch(() => {
      toast.error("Something went wrong, please try again later");
      setState((prev) => ({ ...prev, otpId: null, loader: false, otp: "" }));
    });
};
  const changeScreen = (e, menu) => {
    e.preventDefault(); e.stopPropagation();
    const captchaValue = recaptcha?.current?.getValue();
    if (!captchaValue) { toast.warning("Please verify the reCAPTCHA!"); return; }
    if (menu === 0) saveInitDetails(e, menu);
    else updateState("activeMenu", menu + 1);
  };

  const onContinueWithExtUsr = () => {
    setShowExtUsr(false);
    setState((prev) => ({ ...prev, phoneNumber: extUsr.mobileNumber, email: extUsr.email, userName: extUsr.displayName, activeMenu: 2 }));
  };

  const sortedSubscriptions = [...(subscriptionList || [])].sort((a, b) => a.prize - b.prize);
  const selectedPlan = subscriptionList?.find((s) => s.id === state.subscriptionId);
  const isFreeTrialSelected = selectedPlan?.subscribeType === "FREE_TRIAL";

  const startFreeTrial = async () => {
    if (!state.subscriptionId) {
      toast.warning("Please select the Free Trial plan first");
      return;
    }

    updateState("loader", true);

    const freeOrderId = `Free_${state.initId || state.userId || Date.now()}`;

    let paymentRowId = null;

    try {
      const paymentPayload = {
        paymentId: freeOrderId,
        orderId: freeOrderId,
        razorpaySignature: `FREE_${freeOrderId}`,
        amount: 0,
        realAmount: 0,
        razorpayAmount: 0,
        type: "Service-Register",
        subscriptionId: state.subscriptionId,
        email: state.email,
        name: state.userName,
        serviceId: null,
        status: "success",
      };
      if (state.userId) paymentPayload.userId = state.userId;

      const apiCall = state.userId
        ? MyYuukke.savePaymentInforMation(paymentPayload)
        : MyYuukke.savePaymentInforMationWithoutAuth(paymentPayload);

      const payRes = await apiCall;
      paymentRowId = payRes?.data?.data?.id;
      console.log("✅ Free trial payment record saved, row id:", paymentRowId);
    } catch (err) {
      console.error("❌ Free trial payment record failed:", err?.response?.data || err.message || err);
    }

    if (!paymentRowId) {
      toast.error("Couldn't record your free trial. Please try again.");
      updateState("loader", false);
      return;
    }

    try {
      await updateDetailsAsync(paymentRowId);
    } catch (err) {
      console.error("❌ Free trial registration failed:", err?.response?.data || err.message || err);
      toast.error("Something went wrong completing your free trial. Please try again.");
      updateState("loader", false);
    }
  };

  const updateDetailsAsync = (paymentId) => {
    const resolvedCategory = categoryList && categoryList.length > 0 ? [{ label: categoryList[0].label, value: categoryList[0].value, pId: categoryList[0].pId || 1 }] : DEFAULT_CATEGORY;
    const resolvedLanguage = languagelist && languagelist.length > 0 ? [{ label: languagelist[0].label, value: languagelist[0].value }] : DEFAULT_LANGUAGE;
    const formData = new FormData();
    formData.append("firstName", state.userName.trim());
    formData.append("lastName", state.userName.trim().substring(0, 1));
    formData.append("email", state.email);
    formData.append("phoneNumber", state.phoneNumber);
    formData.append("serviceName", DEFAULT_SERVICE_NAME);
    formData.append("description", DEFAULT_ABOUT);
    formData.append("businessName", state.userName.trim());
    formData.append("language", JSON.stringify({ data: resolvedLanguage.map((item) => ({ language: item.label, id: item.value })) }));
    formData.append("categoryV2", JSON.stringify({ data: resolvedCategory.map((item) => ({ primaryId: item.pId, categoryId: item.value })) }));
    formData.append("categoryId", resolvedCategory[0]?.value);
    formData.append("categoryName", resolvedCategory[0]?.label);
    if (state.directoryId) formData.append("directoryId", state.directoryId);
    formData.append("subscriptionId", state.subscriptionId);
    formData.append("paymentId", paymentId);
    if (state.initId) formData.append("initId", state.initId);
    if (referralCode) formData.append("referralCode", referralCode);

    const config = { headers: { "content-type": "multipart/form-data" } };
    return ProfileService.directoryAndServiceRegister(formData, config).then((response) => {
      console.log("directoryAndServiceRegister response:", response.data);
      if (response.data.status !== 1) {
        toast.error(response.data.message || "Something went wrong, please try again later");
        setState((prev) => ({ ...prev, otpId: null, loader: false, otp: "", subscriptionId: null }));
        throw new Error(response.data.message || "Registration failed with status " + response.data.status);
      }
      setState((prev) => ({ ...prev, otpId: null, loader: false, otp: "", activeMenu: prev.activeMenu + 1, subscriptionId: null }));
      return response;
    });
  };

  return (
    <>
      <BlockUi tag="div" message="Loading, please wait" style={{ minHeight: "100%" }} className="wallLoadingPage pb-3" blocking={state.loader}>
        {state.activeMenu === 0 && (
          <>
            <h2 className="yk2-form-title">Let's get started 🚀</h2>
            <p className="yk2-form-subtitle">Join 500+ women entrepreneurs. Set up your profile in minutes.</p>
            <FormStepBar active={0} total={4} />
            <form onSubmit={(e) => changeScreen(e, 0)}>
              <div className="yk2-field-group">
                <div className="yk2-field">
                  <label>What's Your Name? *</label>
                  <input type="text" placeholder="Kavitha Menon" value={state.userName} onChange={(e) => updateState("userName", e.target.value)} required />
                </div>
                <div className="yk2-field">
                  <label>Where Can We Reach You By Email? *</label>
                  <input type="email" placeholder="kavitha@example.com" value={state.email} onChange={(e) => updateState("email", e.target.value)} required />
                </div>
                <div className="yk2-field">
                  <label>And Your Phone Number? *</label>
                  <div style={{ marginTop: "4px" }}>
                    <PhoneInput country={"in"} containerClass="input-dark" value={state.phoneNumber} placeholder="" countryCodeEditable={false} onChange={(phone) => updateState("phoneNumber", phone)} required />
                  </div>
                  <span className="yk2-field-hint">We'll send your OTP to this number</span>
                </div>
              </div>
              <div style={{ background: "#fff9f7", border: "1px solid #f0dde3", borderRadius: 10, padding: "0.85rem 1rem", fontSize: "0.85rem", color: "#6b4550", marginBottom: "1.25rem", lineHeight: 1.65 }}>
                ✏️ You can update your <strong>business name, category, language, and service details</strong> anytime from your profile after signing up.
              </div>
              <div style={{ marginTop: "1rem", marginBottom: "1rem", minHeight: "78px", display: "flex", justifyContent: "center" }}>
                <ReCAPTCHA ref={recaptcha} sitekey={REACT_APP_SITE_KEY} theme="light" size="normal" />
              </div>
              <button type="submit" className="yk2-form-submit yk2-rpl" onClick={ripple}>Get OTP →</button>
              <p className="yk2-form-privacy">🔒 Your data is encrypted and never shared.</p>
            </form>
          </>
        )}

        {state.activeMenu === 1 && (
          <div className="card p-3 m-auto" style={{ maxWidth: "30rem", textAlign: "start", boxShadow: "none" }}>
            <h2 className="yk2-form-title">Verify Your Number</h2>
            <p className="yk2-form-subtitle">OTP sent to <strong>+{state.phoneNumber?.slice(2)}</strong>. Enter the 6-digit code below.</p>
            <FormStepBar active={1} total={4} />
            <Card className="profileCard align-items-center" style={{ border: "none" }}>
              <p style={{ fontSize: "12px" }} className="text-secondary mt-3 text-center">Please enter your OTP!</p>
              <div className="p-3 d-flex g-1 align-items-center justify-content-center service-otp">
                <OtpInput length={6} onOtpSubmit={(otp) => updateState("otp", otp)} />
              </div>
              <button className="yk2-form-submit yk2-rpl mt-3 mb-3" style={{ width: "fit-content", padding: ".75rem 2rem" }} onClick={(e) => { ripple(e); verifyOtp(); }}>Verify OTP →</button>
            </Card>
            <p className="yk2-form-privacy" style={{ marginTop: ".75rem" }}>
              Didn't receive it? <span style={{ color: "#a00030", cursor: "pointer", fontWeight: 600 }} onClick={() => sendOtp(null, 0, true)}>Resend OTP</span>
            </p>
          </div>
        )}

        {state.activeMenu === 2 && (
          <div>
            <header style={{textAlign: "center", marginBottom: "2rem"}}>
                <h2 className="yk2-form-title">Choose Your Plan</h2>
                <p className="yk2-form-subtitle">Start free, upgrade anytime — or lock in lifetime access.</p>
                <FormStepBar active={2} total={4} />
            </header>

            {state.subscriptionId && !isFreeTrialSelected && (
              <RazorPay
                email={state.email} name={state.userName} userId={null} contactNumber={state.phoneNumber}
                subscriptionId={state.subscriptionId} amount={state.paymentAmount} type={"Service-Register"}
                updatePaymentStatus={updatePaymentStatus} initId={state.initId} uId={state.userId}
              />
            )}

            <div className="yk2-sub-cards" role="list">
              {sortedSubscriptions.map((data) => {
                const isFree = data.subscribeType === "FREE_TRIAL";
                const isSelected = state.subscriptionId === data.id;
                const badge = data.subscribeType === "FREE_TRIAL" ? "TRY FREE" : data.subscribeType === "YEARLY" ? "MOST FLEXIBLE" : data.subscribeType === "LIFETIME" ? "BEST VALUE" : null;
                const badgeColor = data.subscribeType === "YEARLY" ? "#7a0024" : data.subscribeType === "LIFETIME" ? "#15803d" : "#2563eb";
                const duration = data.subscribeType === "FREE_TRIAL" ? "Free Trial" : data.subscribeType === "YEARLY" ? "Billed Yearly" : data.subscribeType === "LIFETIME" ? "One-Time Payment" : "";
                const billing = data.subscribeType === "FREE_TRIAL" ? "No credit card required" : data.subscribeType === "YEARLY" ? "Renews every 365 days" : data.subscribeType === "LIFETIME" ? "Pay once, use forever" : "";
                const priceSuffix = data.subscribeType === "YEARLY" ? "/year" : data.subscribeType === "LIFETIME" ? "one-time" : "";

                return (
                  <article
                    key={data.id}
                    role="listitem"
                    className={`yk2-sub-card${isSelected ? " selected" : ""}`}
                    onClick={() => isFree ? updateState("subscriptionId", data.id) : openPaymentPage(data.prize, data.id)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          isFree ? updateState("subscriptionId", data.id) : openPaymentPage(data.prize, data.id);
                        }
                    }}
                    tabIndex={0}
                  >
                    {badge && (
                      <span className="yk2-sub-badge" style={{ backgroundColor: badgeColor }}>
                        {badge}
                      </span>
                    )}
                    <header className="yk2-sub-header">
                        <h3 className="yk2-sub-card-name">{data.subscriptionName}</h3>
                        {duration && <div className="yk2-sub-duration">{duration}</div>}
                        {billing && <div className="yk2-sub-billing">{billing}</div>}
                    </header>
                    <div className="yk2-sub-card-pricing">
                        {isFree ? (
                          <div className="yk2-sub-price" style={{ color: "#2563eb" }}>Free</div>
                        ) : (
                          <>
                            <div className="yk2-sub-price">₹{data.prize.toLocaleString("en-IN")}</div>
                            <div className="yk2-sub-price-suffix">{priceSuffix}</div>
                          </>
                        )}
                    </div>
                    <div
                        className="yk2-sub-card-desc"
                        dangerouslySetInnerHTML={{ __html: data.subscribeDescription.replace(/<p><\/p>/g, "<br/>") }}
                    />
                    <footer className="yk2-sub-card-footer">
                        {isSelected ? (
                            isFree ? (
                              <button
                                type="button"
                                className="yk2-sub-status-selected"
                                style={{ border: "none", cursor: "pointer", width: "100%", background: "transparent", font: "inherit" }}
                                onClick={(e) => { e.stopPropagation(); ripple(e); startFreeTrial(); }}
                              >
                                ✓ Selected — Tap to Start Free Trial →
                              </button>
                            ) : (
                              <div className="yk2-sub-status-selected">✓ Selected — Pay above</div>
                            )
                        ) : (
                            <div className="yk2-sub-status-unselected">Select Plan →</div>
                        )}
                    </footer>
                  </article>
                );
              })}
            </div>

            <p className="yk2-form-privacy" style={{textAlign: "center", marginTop: "1rem"}}>
              🔒 Secured via Razorpay · UPI · Cards · Net Banking
            </p>

            {isFreeTrialSelected && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                  <button className="yk2-form-submit yk2-rpl mt-3" style={{ maxWidth: "300px" }} onClick={(e) => { ripple(e); startFreeTrial(); }}>
                    Start Free Trial →
                  </button>
              </div>
            )}
          </div>
        )}

        {state.activeMenu === 3 && (
          <div style={{ padding: "2rem 0 0" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "2.25rem" }}>
              {[{ icon: "👤", label: "Details" }, { icon: "📱", label: "Verify" }, { icon: "💳", label: "Payment" }, { icon: "✓", label: "Done" }].map((s, i) => (
                <React.Fragment key={i}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flex: 1 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#7a0024", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#fff" }}>{s.icon}</div>
                    <span style={{ fontSize: 11, color: "#7a0024", fontWeight: 500 }}>{s.label}</span>
                  </div>
                  {i < 3 && <div style={{ flex: 1, height: 1.5, background: "#f0dde3", marginBottom: 16 }} />}
                </React.Fragment>
              ))}
            </div>
            <div style={{ border: "0.5px solid #f0dde3", borderRadius: 16, padding: "2rem 1.75rem 1.75rem", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
              <div style={{ position: "relative", width: 64, height: 64, marginBottom: "1.25rem" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", border: "1.5px solid #f0dde3", background: "#fff8f9", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontSize: 28 }}>🏅</span></div>
                <div style={{ position: "absolute", bottom: -2, right: -4, width: 22, height: 22, borderRadius: "50%", background: "#7a0024", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontSize: 11, color: "#fff" }}>✓</span></div>
              </div>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 500, margin: "0 0 0.35rem" }}>You're all set!</h3>
              <p style={{ fontSize: 13, color: "#6b4550", margin: "0 0 1.5rem", lineHeight: 1.65, maxWidth: 300 }}>
                Welcome to Yuukke. We'll send your login details to <strong style={{ color: "#7a0024" }}>+{state.phoneNumber}</strong> on WhatsApp.
              </p>
              <div style={{ width: "100%", maxWidth: 320, marginBottom: "1.75rem" }}>
                {["Profile created & live", "Payments & GST invoicing ready", "WhatsApp reminders activated"].map((text, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, marginBottom: 6, background: "#fff8f9", border: "0.5px solid #f0dde3" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#7a0024", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span style={{ fontSize: 11, color: "#fff" }}>✓</span></div>
                    <span style={{ fontSize: 13, color: "#7a0024", fontWeight: 500 }}>{text}</span>
                  </div>
                ))}
              </div>
              <hr style={{ width: "100%", maxWidth: 320, border: "none", borderTop: "0.5px solid #f0dde3", margin: "0 0 1.5rem" }} />
              <button className="yk2-rpl" style={{ width: "100%", maxWidth: 280, height: 46, borderRadius: 12, background: "#7a0024", color: "#fff", fontSize: 14, fontWeight: 500, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={(e) => { ripple(e); window.location.href = "/"; }}>
                Go to your dashboard →
              </button>
              <p style={{ marginTop: "0.9rem", fontSize: 12, color: "#9a7a82" }}>Need help? <span style={{ color: "#7a0024", cursor: "pointer", fontWeight: 500 }} onClick={() => window.open("https://wa.me/919150625864", "_blank")}>Chat with us on WhatsApp</span></p>
            </div>
          </div>
        )}
      </BlockUi>

      <PhoneUpdateModal open={showPhoneUpdate} onClose={() => setShowPhoneUpdate(false)} existingUser={phoneUpdateUser} newPhone={state.phoneNumber} onSuccess={(verifiedNewPhone) => { setShowPhoneUpdate(false); setState((prev) => ({ ...prev, email: phoneUpdateUser.email, userName: phoneUpdateUser.displayName, userId: phoneUpdateUser.userId, phoneNumber: verifiedNewPhone, activeMenu: 2 })); }} />

      <Dialog open={showExtUsr} onClose={() => setShowExtUsr(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User Already Exists</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="row" alignItems="center" p={2} style={{ border: "1px solid hsl(220 13% 91%)", borderRadius: "8px" }}>
            <Avatar src={`${API_URL}auth/image/${extUsr.profilePhoto}`} style={{ width: 80, height: 80, marginRight: 16 }} />
            <Box display="flex" flexDirection="column">
              <Typography variant="subtitle1">{extUsr.displayName || ""}</Typography>
              <Typography variant="body1">{extUsr.email || ""}</Typography>
              <Typography variant="body2" color="textSecondary">{extUsr.mobileNumber || ""}</Typography>
            </Box>
          </Box>
          <Typography variant="subtitle1" color="textSecondary" align="center" style={{ marginTop: 12 }}>You already have an account. Shall we continue with this account?</Typography>
          <Box display="flex" justifyContent="center" mt={1}>
            <Typography variant="caption" color="textSecondary" align="center">Note: Please use this existing account credentials for all future services. If you don't remember your credentials, use "Forgot Password" with this email: <strong>{extUsr.email}</strong></Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExtUsr(false)} color="secondary" variant="outlined" style={{ color: "brown", background: "transparent" }}>Cancel</Button>
          <Button onClick={onContinueWithExtUsr} color="primary" variant="contained">Continue</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SERVICE REFERRAL PAGE
// ─────────────────────────────────────────────────────────────────────────────
function ServiceReferralPage({ t }) {
  const { referralCode } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [status, setStatus] = useState("loading"); // loading | valid | invalid
  const [referrerName, setReferrerName] = useState("");
  const [resolvedCode, setResolvedCode] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [manualChecking, setManualChecking] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [loginModel, setLoginModel] = useState(false);
  const [forceLoginEmail, setForceLoginEmail] = useState(null);

  const { categoryList, subscriptionList } = useSelector((state) => state.event);
  const { languagelist } = useSelector((state) => state.commonApi);

  useEffect(() => { injectStylesOnce(); }, []);

  useEffect(() => {
    if (status !== "valid") return;
    dispatch(getSubscriptionList("service"));
    dispatch(getAllCommonLanguageList());
    dispatch(getCategoryListV2());
  }, [status, dispatch]);

  const runValidation = (codeToCheck) => {
    setStatus("loading");
    ServiceReferral.validateReferralCode({ referralCode: codeToCheck })
      .then((res) => {
        const data = res?.data?.data || {};
        if (data.valid) {
          setStatus("valid");
          setResolvedCode(codeToCheck);
          setReferrerName(data.referrerName || "");
          sessionStorage.setItem("pendingReferralCode", codeToCheck);
        } else {
          tryLookalikesThenFail(codeToCheck);
        }
      })
      .catch(() => tryLookalikesThenFail(codeToCheck));
  };

  const tryLookalikesThenFail = async (codeToCheck) => {
    const variants = buildLookalikeVariants(codeToCheck).filter((v) => v !== codeToCheck);
    for (const variant of variants) {
      try {
        const res = await ServiceReferral.validateReferralCode({ referralCode: variant });
        const data = res?.data?.data || {};
        if (data.valid) {
          setStatus("valid");
          setResolvedCode(variant);
          setReferrerName(data.referrerName || "");
          sessionStorage.setItem("pendingReferralCode", variant);
          return;
        }
      } catch {
        // keep trying remaining variants
      }
    }
    setStatus("invalid");
    setManualCode(codeToCheck);
  };

  useEffect(() => {
    const normalized = normalizeReferralCode(referralCode);
    if (!normalized) {
      setStatus("invalid");
      return;
    }
    runValidation(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode]);

  useEffect(() => {
    if (status !== "invalid" || !manualCode || manualCode.length < 4) {
      setManualResult(null);
      return;
    }
    setManualChecking(true);
    const timer = setTimeout(() => {
      ServiceReferral.validateReferralCode({ referralCode: manualCode })
        .then((res) => {
          const data = res?.data?.data || {};
          setManualResult({ valid: !!data.valid, referrerName: data.referrerName || "" });
        })
        .catch(() => setManualResult({ valid: false }))
        .finally(() => setManualChecking(false));
    }, 450);
    return () => clearTimeout(timer);
  }, [manualCode, status]);

  const applyManualCode = () => {
    if (!manualResult?.valid) return;
    setStatus("valid");
    setResolvedCode(manualCode);
    setReferrerName(manualResult.referrerName || "");
    sessionStorage.setItem("pendingReferralCode", manualCode);
  };

  return (
    <div className="svcrefpg-root yk2-root">
      <div className="svcrefpg-container">
        
        {/* LEFT COLUMN: Hero & Value Prop */}
        <div className="svcrefpg-hero">
          <div className="svcrefpg-hero-content">
            <img src={headerImg} alt="Yuukke" className="svcrefpg-logo" />
            
            {status === "valid" ? (
              <>
                <h1 className="svcrefpg-hero-title">
                  {referrerName ? `${referrerName} invited you!` : "You've been invited!"}
                </h1>
                <p className="svcrefpg-hero-subtitle">
                  Join a community of 500+ women entrepreneurs. Complete your registration to unlock your exclusive referral rewards.
                </p>
                <div className="svcrefpg-code-badge">
                  🎁 {resolvedCode}
                </div>
              </>
            ) : status === "loading" ? (
              <>
                <h1 className="svcrefpg-hero-title">Checking Invitation</h1>
                <p className="svcrefpg-hero-subtitle">Please wait a moment while we verify your referral code...</p>
              </>
            ) : (
              <>
                <h1 className="svcrefpg-hero-title">Oops!</h1>
                <p className="svcrefpg-hero-subtitle">We couldn't recognize this invite code. You can update it on the right to claim your reward.</p>
              </>
            )}

            {/* NEW ADDITIONS: Value Prop & Trust Elements */}
            <div className="svcrefpg-benefits">
              <div className="svcrefpg-benefit-item">
                <span className="svcrefpg-benefit-icon">🚀</span>
                <div className="svcrefpg-benefit-text">
                  <h4>Grow Your Business</h4>
                  <p>Get discovered by thousands of active customers looking for your services.</p>
                </div>
              </div>
              
              <div className="svcrefpg-benefit-item">
                <span className="svcrefpg-benefit-icon">🤝</span>
                <div className="svcrefpg-benefit-text">
                  <h4>Empowering Community</h4>
                  <p>Connect, collaborate, and learn from 500+ inspiring women entrepreneurs.</p>
                </div>
              </div>
              
              <div className="svcrefpg-benefit-item">
                <span className="svcrefpg-benefit-icon">💳</span>
                <div className="svcrefpg-benefit-text">
                  <h4>Seamless Payments</h4>
                  <p>Integrated invoicing and secure payments directly to your bank account.</p>
                </div>
              </div>
            </div>

            <div className="svcrefpg-trust">
              <p>Join {referrerName ? referrerName + ' and' : ''} 500+ others already growing on Yuukke.</p>
            </div>
            {/* END NEW ADDITIONS */}

          </div>
        </div>

        {/* RIGHT COLUMN: Form or Error States */}
        <div className="svcrefpg-form-area">
          {status === "loading" && (
            <div style={{ textAlign: "center" }}>
              <div className="svcrefpg-icon-wrap loading" style={{ margin: "0 auto 1.5rem" }}>
                <div className="svcrefpg-spinner" />
              </div>
              <h2 className="svcrefpg-title">Verifying code…</h2>
              <p className="svcrefpg-sub">Hang tight, connecting securely.</p>
            </div>
          )}

          {status === "valid" && (
            <div className="yk2-form-card">
              <RegistrationForm
                subscriptionList={subscriptionList}
                languagelist={languagelist}
                categoryList={categoryList}
                referralCode={resolvedCode}
                onForceLogin={(email) => { setForceLoginEmail(email); setLoginModel(true); }}
              />
            </div>
          )}

          {status === "invalid" && (
            <>
              <div className="svcrefpg-icon-wrap invalid">⚠️</div>
              <h2 className="svcrefpg-title">Invalid Referral Code</h2>
              <p className="svcrefpg-sub">
                The link you used has a code we don't recognize. Double-check it below—codes don't use spaces or symbols.
              </p>

              <div className="svcrefpg-fix-box">
                <label className="svcrefpg-fix-label">Enter Referral Code</label>
                <div className={`svcrefpg-fix-row ${manualResult?.valid ? "is-valid" : ""}`}>
                  <input
                    type="text"
                    className="svcrefpg-fix-input"
                    value={manualCode}
                    maxLength={12}
                    onChange={(e) => setManualCode(normalizeReferralCode(e.target.value))}
                    placeholder="e.g. YUUKKE24AB"
                  />
                  {manualChecking && <div className="svcrefpg-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />}
                  {!manualChecking && manualResult?.valid && <span style={{ color: "#22c55e", fontSize: "1.2rem" }}>✓</span>}
                  {!manualChecking && manualResult && !manualResult.valid && manualCode && (
                    <span style={{ color: "#ef4444", fontSize: "1.2rem" }}>✕</span>
                  )}
                </div>
                <span className="svcrefpg-fix-hint">
                  {manualResult?.valid
                    ? `✓ Valid — referred by ${manualResult.referrerName || "a Yuukke member"}`
                    : "Codes are usually 8–10 letters and numbers."}
                </span>
              </div>

              <button
                className="svcrefpg-btn"
                disabled={!manualResult?.valid}
                style={!manualResult?.valid ? { background: "#e5c3cc", cursor: "not-allowed" } : undefined}
                onClick={applyManualCode}
              >
                Apply & Continue →
              </button>
              <button
                className="svcrefpg-btn-outline"
                onClick={() => {
                  sessionStorage.removeItem("pendingReferralCode");
                  setStatus("valid");
                  setResolvedCode("");
                  setReferrerName("");
                }}
              >
                Skip & Sign Up Without Code
              </button>
            </>
          )}
        </div>
      </div>

      {loginModel && <ServiceLoginModel closeModel={() => setLoginModel(false)} presetEmail={forceLoginEmail} />}
    </div>
  );
}

export default withTranslation()(ServiceReferralPage);